import React from 'react';
import { st, classes } from './MarketingPageLayoutContent.st.css';
import { dataHooks, size } from './constants';
import { MarketingPageLayoutContentProps } from './MarketingPageLayoutContent.types';

import { Divider } from '../Divider';
import { Text, SIZES as TEXT_SIZES } from '../Text';
import { Heading, SIZE as HEADING_SIZE } from '../Heading';
import { Box } from '../Box';
import { WixDesignSystemContext } from '../WixDesignSystemProvider/context';
import { MarketingPageLayoutContext } from '../MarketingPageLayout/context';
import { isString } from '../utils/StringUtils';

const sizesMap = {
  overline: {
    [size.medium]: TEXT_SIZES.small,
    [size.large]: TEXT_SIZES.medium,
  },
  title: {
    [size.medium]: HEADING_SIZE.LARGE,
    [size.large]: HEADING_SIZE.EXTRA_LARGE,
  },
  subtitle: {
    [size.medium]: HEADING_SIZE.SMALL,
    [size.large]: HEADING_SIZE.MEDIUM,
  },
  content: {
    [size.medium]: TEXT_SIZES.small,
    [size.large]: TEXT_SIZES.medium,
  },
} as const;

/** This component is used in the MarketingPageLayout component. It includes all the content of the page. */
const MarketingPageLayoutContent = ({
  dataHook,
  className,
  size = 'large',
  overline,
  title,
  subtitle,
  content,
  actions,
}: MarketingPageLayoutContentProps) => {
  const { mobile } = React.useContext(WixDesignSystemContext);
  const { layout } = React.useContext(MarketingPageLayoutContext);

  const imageTop = layout === 'imageTop';

  const overlineElement = overline && (
    <div data-hook={dataHooks.overlineContainer}>
      <Box color="D20">
        {isString(overline) ? (
          <Text
            dataHook={dataHooks.overline}
            size={sizesMap.overline[size]}
            skin="standard"
            secondary
          >
            {overline}
          </Text>
        ) : (
          overline
        )}
      </Box>
      <div className={classes.overlineDividerContainer}>
        <Divider />
      </div>
    </div>
  );

  const titleElement = title && (
    <Box dataHook={dataHooks.titleContainer} className={classes.titleContainer}>
      {isString(title) ? (
        <Heading
          dataHook={dataHooks.title}
          size={sizesMap.title[size]}
          className={classes.title}
        >
          {title}
        </Heading>
      ) : (
        title
      )}
    </Box>
  );

  const subtitleElement = subtitle && (
    <Box
      dataHook={dataHooks.subtitleContainer}
      className={classes.subtitleContainer}
      marginTop={imageTop ? undefined : 'SP2'}
    >
      {isString(subtitle) ? (
        <Heading
          dataHook={dataHooks.subtitle}
          size={sizesMap.subtitle[size]}
          className={classes.subtitle}
        >
          {subtitle}
        </Heading>
      ) : (
        subtitle
      )}
    </Box>
  );

  const contentElement = content && (
    <Box
      dataHook={dataHooks.contentContainer}
      className={classes.contentBox}
      color="D10"
    >
      {isString(content) ? (
        <Text
          dataHook={dataHooks.content}
          size={sizesMap.content[size]}
          skin="standard"
          className={classes.content}
        >
          {content}
        </Text>
      ) : (
        content
      )}
    </Box>
  );

  const actionsElement = actions && (
    <Box
      dataHook={dataHooks.actions}
      marginTop={!imageTop && mobile ? 'SP4' : undefined}
      className={classes.actionsContainer}
      children={actions}
    />
  );

  const rootClassName = st(classes.root, { layout, size, mobile }, className);

  if (imageTop) {
    return (
      <div data-hook={dataHook} className={rootClassName}>
        <div className={classes.textColumn}>
          {overlineElement}
          {titleElement}
          {subtitleElement}
          {actionsElement}
        </div>

        <div className={classes.contentColumn}>{contentElement}</div>
      </div>
    );
  }

  return (
    <div data-hook={dataHook} className={rootClassName}>
      {overlineElement}
      {titleElement}
      {subtitleElement}
      {contentElement}
      {actionsElement}
    </div>
  );
};

MarketingPageLayoutContent.displayName = 'MarketingPageLayoutContent';

export { MarketingPageLayoutContent };
